/**
 * O apply por recurso (spec 2026-10-01-acompanhamento-do-apply-por-recurso,
 * direção "Lista por recurso" escolhida pelo Carlos): barra pela contagem,
 * o que está em curso com o tempo DO TERRAFORM, e a lista por categoria.
 * Estado sempre com ícone + rótulo (DESIGN_SYSTEM §925), ícones Tabler (SVG).
 */
import { useState } from 'react'
import { Group, Progress, Stack, Text, UnstyledButton } from '@mantine/core'
import {
  IconAlertTriangle, IconArrowsSplit, IconBox, IconBucket, IconCertificate, IconCheck,
  IconChevronDown, IconChevronRight, IconCircleDashed, IconDatabase, IconKey, IconLambda,
  IconLoader2, IconLock, IconNetwork, IconPlayerStop, IconServer, IconWorldWww,
} from '@tabler/icons-react'
import { CATEGORIAS, type Categoria, type EstadoDoRecurso, type RecursoDoApply } from './recursosDoApply'
import { FONTE_DADO } from './Dado'

const ICONE: Record<Categoria, typeof IconBox> = {
  banco: IconDatabase, instancia: IconServer, balanceador: IconArrowsSplit, dns: IconWorldWww,
  rede: IconNetwork, permissao: IconKey, funcao: IconLambda, segredo: IconLock,
  armazenamento: IconBucket, certificado: IconCertificate, outros: IconBox,
}

const VERBO = {
  criar: ['criando', 'criado'], alterar: ['alterando', 'alterado'],
  destruir: ['destruindo', 'destruído'], recriar: ['recriando', 'recriado'],
} as const

// A cor com o TOM, resolvida (o ícone usa a variável CSS, e `--mantine-color-red`
// sem tom não existe — o ícone sumia). `red.8` é o `#e03131` do danger
// (DESIGN_SYSTEM §181); `inovareTeal.7` é o `primaryShade` do tema.
const COR: Record<EstadoDoRecurso, string> = {
  feito: 'green.6', em_curso: 'inovareTeal.7', aguardando: 'dimmed', interrompido: 'dimmed', erro: 'red.8',
}
const varDaCor = (c: string) => `var(--mantine-color-${c.replace('.', '-')})`
const ICONE_DO_ESTADO: Record<EstadoDoRecurso, typeof IconBox> = {
  feito: IconCheck, em_curso: IconLoader2, aguardando: IconCircleDashed,
  interrompido: IconPlayerStop, erro: IconAlertTriangle,
}
// O selo da categoria é o PIOR estado presente (D3).
const GRAVIDADE: EstadoDoRecurso[] = ['erro', 'interrompido', 'em_curso', 'aguardando', 'feito']

function rotuloDoEstado(r: RecursoDoApply): string {
  if (r.estado === 'feito') return VERBO[r.acao][1]
  if (r.estado === 'em_curso') return r.decorrido ? `${VERBO[r.acao][0]} há ${r.decorrido}` : VERBO[r.acao][0]
  return r.estado
}

// O estado nunca encolhe nem quebra (TASK-24879: "criando há 03m00s" saía em
// duas linhas espremidas ao lado do endereço do RDS). Quem cede espaço é o
// endereço, que tem o corte pelo começo.
function Estado({ r }: { r: RecursoDoApply }) {
  const Icone = ICONE_DO_ESTADO[r.estado]
  return (
    <Group gap={4} wrap="nowrap" data-estado style={{ flexShrink: 0 }}>
      <Icone size={14} color={varDaCor(COR[r.estado])} aria-hidden />
      <Text size="xs" c={COR[r.estado]} style={{ whiteSpace: 'nowrap' }}>{rotuloDoEstado(r)}</Text>
    </Group>
  )
}

/** O endereço cortado pelo COMEÇO (TASK-24879). Os endereços de um mesmo
 * módulo só diferem no fim — `module.asg.aws_autoscaling_group.auto_scaling_group["portal"]`
 * e `...["apiacl"]` —, e o corte no fim deixava as cinco linhas idênticas.
 * `direction: rtl` põe as reticências à esquerda; o `<bdi dir="ltr">` mantém a
 * ordem do texto (sem ele colchetes e aspas trocam de lado). O endereço inteiro
 * fica no `title`. Mesma fonte e tamanho do `Dado`. */
function Endereco({ endereco }: { endereco: string }) {
  return (
    <Text component="span" data-dado size="0.8rem" title={endereco}
          style={{
            fontFamily: FONTE_DADO, direction: 'rtl', textAlign: 'left',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            minWidth: 0, flex: '1 1 auto',
          }}>
      <bdi dir="ltr">{endereco}</bdi>
    </Text>
  )
}

function Item({ r }: { r: RecursoDoApply }) {
  return (
    <Stack gap={0} align="stretch" style={{ width: '100%' }}>
      <Group gap="xs" wrap="nowrap" align="center" style={{ minWidth: 0 }}>
        <Estado r={r} />
        <Endereco endereco={r.endereco} />
      </Group>
      {r.erro && <Text size="xs" c="red.8">{r.erro}</Text>}
    </Stack>
  )
}

export interface AcompanhamentoPorRecursoProps {
  recursos: RecursoDoApply[]
  erroSolto: string | null
  feitas: number
  total: number
}

export function AcompanhamentoPorRecurso({ recursos, erroSolto, feitas, total }: AcompanhamentoPorRecursoProps) {
  const [abertas, setAbertas] = useState<Set<Categoria>>(() => new Set())
  const emCurso = recursos.filter((r) => r.estado === 'em_curso')
  const pct = total > 0 ? Math.min(100, Math.round((feitas / total) * 100)) : 0
  const alternar = (c: Categoria) => setAbertas((s) => {
    const n = new Set(s); if (n.has(c)) n.delete(c); else n.add(c); return n
  })

  return (
    <Stack gap={6} align="stretch" style={{ width: '100%' }}>
      <Stack gap={2} align="flex-start">
        <Text size="sm">{feitas} de {total} mudanças feitas</Text>
        <Progress value={pct} color="inovareTeal" size="sm" style={{ width: '100%' }}
                  aria-label="mudanças feitas" />
      </Stack>
      {erroSolto && <Text size="xs" c="red.8">{erroSolto}</Text>}
      {emCurso.length > 0 && (
        <Stack gap={2} align="stretch">
          <Text size="xs" fw={500}>Em curso</Text>
          {emCurso.map((r) => {
            const Icone = ICONE[r.categoria]
            return (
              <Group key={r.endereco} gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                <Icone size={16} aria-hidden style={{ flexShrink: 0 }} />
                <Estado r={r} />
                <Endereco endereco={r.endereco} />
              </Group>
            )
          })}
        </Stack>
      )}
      <Stack gap={2} align="flex-start">
        {CATEGORIAS.map((c) => {
          const daqui = recursos.filter((r) => r.categoria === c.chave)
          if (daqui.length === 0) return null
          const feitos = daqui.filter((r) => r.estado === 'feito').length
          const pior = GRAVIDADE.find((e) => daqui.some((r) => r.estado === e))!
          const Icone = ICONE[c.chave]
          const Seta = abertas.has(c.chave) ? IconChevronDown : IconChevronRight
          const SeloIcone = ICONE_DO_ESTADO[pior]
          return (
            <Stack key={c.chave} gap={2} align="flex-start" style={{ width: '100%' }}>
              <UnstyledButton onClick={() => alternar(c.chave)} aria-expanded={abertas.has(c.chave)}>
                <Group gap="xs" wrap="nowrap">
                  <Seta size={14} aria-hidden />
                  <Icone size={16} aria-hidden />
                  <Text size="sm">{c.rotulo}</Text>
                  <Text size="xs" c="dimmed">{feitos} de {daqui.length}</Text>
                  <SeloIcone size={14} aria-label={pior}
                             color={varDaCor(COR[pior])} />
                </Group>
              </UnstyledButton>
              {/* Só desenha aberta: o `Collapse` mantém o conteúdo no DOM, e o
                  mesmo recurso apareceria duas vezes (aqui e em "Em curso"). */}
              {abertas.has(c.chave) && (
                <Stack gap={2} pl="lg" align="stretch" style={{ width: '100%' }}>
                  {daqui.map((r) => <Item key={r.endereco} r={r} />)}
                </Stack>
              )}
            </Stack>
          )
        })}
      </Stack>
    </Stack>
  )
}
